import { logger } from '@/sentry.config'
import { v4 as uuidv4 } from 'uuid'
import ProviderAPIClient from '../ProviderAPIClient'

/** Auth0-compatible JWT from POST /auth/epic-provision (used for provider-api when in Epic launch). */
const PROVIDER_API_JWT_KEY = 'providerApiJwtToken'

/**
 * Fixed localStorage key for all Epic FHIR sessions.
 * All sessions (keyed by OAuth state) live under this single entry so the same
 * data is found regardless of which `state` value is in the URL at construction time.
 */
const SESSIONS_STORAGE_KEY = 'epic_fhir_sessions'

/** Dispatched on `window` after Epic sessions are written to localStorage so UI can re-read the singleton. */
export const EPIC_FHIR_SESSIONS_UPDATED_EVENT = 'epic-fhir-sessions-updated'

/**
 * Buffer subtracted from the hard expiry so the session is treated as expired a
 * little early. This locks the user out before in-flight Epic calls start
 * failing on a truly-expired token.
 */
export const EPIC_ACCESS_TOKEN_EXPIRY_BUFFER_MS = 5 * 60 * 1000

/**
 * How long before the lockout point (expiry minus buffer) to start warning the
 * user so they can save in-progress work. The warning therefore appears at
 * `expiresAt - EPIC_ACCESS_TOKEN_EXPIRY_BUFFER_MS - EPIC_ACCESS_TOKEN_EXPIRY_WARNING_MS`
 * and remains visible until the lockout takes over.
 */
export const EPIC_ACCESS_TOKEN_EXPIRY_WARNING_MS = 5 * 60 * 1000

/**
 * Normalize an incoming `clientHostSource` to a trimmed non-empty string, or
 * `null` when it is `undefined`/`null`/blank. Epic does not always populate it,
 * so `null` is the explicit "unknown host source" signal — consumers must not
 * assume a value is present.
 */
export function normalizeClientHostSource(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return null
  }
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

/**
 * Decode the `exp` (expiration) claim from a JWT and return it as epoch millis,
 * or null when the token is malformed or has no numeric `exp`.
 *
 * Used to read the true expiry of the Epic SMART on FHIR `access_token` (an
 * RS256 JWT with `exp` in seconds). This only reads the unverified payload for a
 * client-side expiry hint — the token itself was issued by our trusted backend
 * exchange, so no signature verification is performed here.
 */
export function decodeJwtExpiryMs(jwt: string | null | undefined): number | null {
  if (!jwt || typeof jwt !== 'string') {
    return null
  }
  const parts = jwt.split('.')
  if (parts.length < 2) {
    return null
  }
  try {
    // Base64url → base64 before decoding.
    const payloadB64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const claims = JSON.parse(atob(payloadB64)) as Record<string, unknown>
    const exp = claims.exp
    if (typeof exp !== 'number' || !Number.isFinite(exp)) {
      return null
    }
    return exp * 1000
  } catch {
    return null
  }
}

/**
 * sessionStorage key that persists the active OAuth `state` across page reloads
 * within the same browser tab. Written after a successful token exchange so that
 * a hard-refresh on /order/* can still locate the correct session.
 */
export const ACTIVE_OAUTH_STATE_STORAGE_KEY = 'epic_smart_oauth_state'

/**
 * Client onboarding / Dynamo partition key from provisioned JWT claims.
 * Auth0 uses `{issuer}/clientConfigKey` (see provider-api /config routes); optional stable claim.
 */
function clientConfigKeyFromJwtClaims(claims: Record<string, unknown>): string | undefined {
  const stable = claims['https://inflectionmedicine.com/clientConfigKey']
  if (typeof stable === 'string' && stable.trim()) {
    return stable.trim()
  }
  for (const key of Object.keys(claims)) {
    if (key.endsWith('/clientConfigKey')) {
      const v = claims[key]
      if (typeof v === 'string' && v.trim()) {
        return v.trim()
      }
    }
  }
  return undefined
}

function applyProvisioningClaimsToSession(session: SessionData, providerApiJwt: string): void {
  try {
    const claims = JSON.parse(atob(providerApiJwt.split('.')[1])) as Record<string, unknown>
    const imdbOrgId = claims['https://inflectionmedicine.com/org_id'] as string | undefined
    if (imdbOrgId && session.imdbOrganizationId !== imdbOrgId) {
      session.imdbOrganizationId = imdbOrgId
      console.log('[EpicAuthClient] imdbOrganizationId set from provisioned JWT:', imdbOrgId)
    }
    const configKey = clientConfigKeyFromJwtClaims(claims)
    if (configKey && session.orgId !== configKey) {
      session.orgId = configKey
      console.log('[EpicAuthClient] session.orgId set from JWT clientConfigKey (Dynamo key):', configKey)
    }
  } catch {
    // JWT decode failed — leave session fields unchanged
  }
}

// Types for the session data
interface SessionData {
  fhirServer: string
  codeVerifier: string
  redirectUri: string
  orgId: string | null
  /** IMDB org UUID from provisioned JWT; orgId stays the Dynamo/client config key (e.g. Epic 11112). */
  imdbOrganizationId?: string | null
  accessToken: string | null
  /**
   * Epoch millis at which `accessToken` expires, decoded from the token's own
   * `exp` claim at exchange time (Epic tokens are ~1h with no refresh). Absent
   * on sessions created before authorization completes, or when the token could
   * not be decoded.
   */
  accessTokenExpiresAt?: number | null
  /** Epic JWT from Lambda (EpicFhirAccessToken); used to call /auth/epic-provision. */
  jwtToken?: string | null
  /**
   * Raw Epic-signed OIDC id_token from the SMART token exchange. IDENTITY artifact
   * for provider-api auth ONLY: forwarded to /auth/epic-provision as X-Epic-Id-Token
   * so the Auth0 Action can verify it against Epic's JWKS. It MUST NEVER be used as a
   * FHIR bearer, put on `accessToken`, or sent to any Epic FHIR endpoint.
   */
  epicIdToken?: string | null
  /** Auth0-compatible JWT from POST /auth/epic-provision; use this for provider-api. */
  [PROVIDER_API_JWT_KEY]?: string | null
  context: {
    fhirPatientId: string | null
    fhirUserId: string | null
    epicUserId: string | null
    epicUserFhirId: string | null
    userEmail: string | null
    userName: string | null
    userLastName: string | null
    providerNpi: string | null
    providerType: string | null
    loginDepartment: string | null
    epicVersion: string | null
    apiKey: string | null // Backward compatibility
    mrnIdentifierSystem: string | null
    clientName: string | null
    /** Epic Contact Serial Number (CSN) for the launch encounter, from the launch context. */
    encounterCsn: string | null
    /**
     * Host application that launched Epic, from the token-exchange response's
     * `clientHostSource`. Normalized via {@link normalizeClientHostSource}: a
     * trimmed non-empty string, or `null` when Epic omits it. Captured for
     * forward-compat (intended for auth use); no behavior keys off it today.
     */
    clientHostSource: string | null
  }
}

interface AuthorizationResponse {
  codeVerifier: string
  codeChallenge: string
  clientId: string
  redirectUri: string
  authorizeUrl: string
  organizationId: string
  fhirServerUrl: string
  authServerUrl: string | null
}

interface AccessTokenResponse {
  message: string
  access_token?: string // Epic FHIR access token
  jwtToken?: string // Provider API JWT token (new)
  token?: string // Alias for jwtToken (backward compatibility)
  /**
   * Raw Epic-signed OIDC id_token (identity artifact for provider-api auth only).
   * Threaded to /auth/epic-provision as X-Epic-Id-Token; never a FHIR bearer.
   */
  epicIdToken?: string
  apiKey?: string // Backward compatibility
  fhirPatientId: string
  fhirUserId: string
  epicUserId: string
  epicUserFhirId: string
  userEmail: string
  userName: string
  userLastName: string
  providerNpi: string
  providerType: string
  loginDepartment: string
  epicVersion: string
  mrnIdentifierSystem: string
  orgId: string
  clientName: string
  encounterCsn?: string
  /**
   * Host application that launched Epic (e.g. Hyperspace, Hyperdrive, mobile),
   * surfaced additively by the token-exchange step. Optional/nullable because
   * Epic may omit it; `null` is the explicit "unknown host source" signal
   * (see {@link normalizeClientHostSource}). Intended for auth use; no behavior
   * keys off it today.
   */
  clientHostSource?: string | null
}

export class EpicAuthClient {
  private _sessions: Map<string, SessionData> = new Map()
  private static instance: EpicAuthClient | null = null
  private readonly instanceState: string
  private providerAPIClient: ProviderAPIClient
  /**
   * In-flight epic-provision promises keyed by session state. Prevents a
   * thundering herd: on Epic launch the SPA fires many concurrent provider-api
   * calls that each need the provisioned token; without this, every one would
   * miss the empty cache and issue its own POST /auth/epic-provision, and that
   * burst of simultaneous token exchanges trips the provider-api circuit breaker
   * (and can cause jti-replay rejects). With this, concurrent callers await the
   * SAME provision call. Cleared in a finally (success OR failure) so a failed
   * provision never wedges future attempts.
   */
  private _inFlightProvision: Map<string, Promise<string | null>> = new Map()

  constructor() {
    // Resolve instanceState in priority order:
    // 1. URL ?state= param (OAuth callback)
    // 2. sessionStorage (post-callback navigation / page reload within same tab)
    // 3. New UUID (true cold start on /launch)
    this.instanceState = this.getOrCreateInstanceState()

    // Check for default client ID (client-specific ones are checked at runtime)
    const requiredVars = ['VITE_EPIC_APP_CLIENT_ID']

    for (const varName of requiredVars) {
      if (!import.meta.env[varName as keyof ImportMetaEnv]) {
        logger.error('Missing required environment variable', { variable: varName })
        throw new Error(`Missing required environment variable: ${varName}`)
      }
    }

    // Load sessions from shared localStorage on initialization
    this.loadSessionsFromStorage()
    this.providerAPIClient = ProviderAPIClient.getInstance()
  }

  /**
   * Resolve instanceState for this client instance.
   *
   * Priority:
   *  1. `?state=` in the current URL (OAuth callback redirect).
   *  2. `sessionStorage[ACTIVE_OAUTH_STATE_STORAGE_KEY]` (page reload after callback).
   *  3. New UUID (fresh launch before Epic OAuth starts).
   */
  private getOrCreateInstanceState(): string {
    if (typeof window === 'undefined') {
      return uuidv4()
    }

    const urlParams = new URLSearchParams(window.location.search)
    const urlState = urlParams.get('state')
    if (urlState) {
      return urlState
    }

    const persisted = sessionStorage.getItem(ACTIVE_OAUTH_STATE_STORAGE_KEY)
    if (persisted) {
      return persisted
    }

    return uuidv4()
  }

  /**
   * Return the currently persisted active OAuth state, or null if none.
   * Useful for route guards that need to check Epic session status without
   * constructing a full client (e.g. before the singleton is initialised).
   */
  static getPersistedOAuthState(): string | null {
    if (typeof window === 'undefined') return null
    return sessionStorage.getItem(ACTIVE_OAUTH_STATE_STORAGE_KEY)
  }

  /**
   * Get singleton instance of EpicAuthClient
   *
   * @returns EpicAuthClient instance
   */
  static getInstance(): EpicAuthClient {
    if (!EpicAuthClient.instance) {
      EpicAuthClient.instance = new EpicAuthClient()
    }
    return EpicAuthClient.instance
  }

  /**
   * Get the instance state that uniquely identifies this EpicAuthClient instance
   *
   * @returns The instance state string
   */
  getInstanceState(): string {
    return this.instanceState
  }

  /**
   * Map OAuth `state` from the Epic callback URL to the key in `_sessions` / localStorage.
   * The value must match what was sent with `/EpicFhirAuthorization`; some environments
   * return a trimmed or encoded variant. If nothing matches, the PKCE session was never
   * stored for this origin (e.g. launch on `localhost` but callback on `127.0.0.1`, or
   * client-config `epicLandingCallback` pointing at a different host than the SPA).
   */
  private resolveSessionKey(state: string): string | null {
    if (!state) {
      return null
    }
    const trimmed = state.trim()
    const candidates: string[] = []
    for (const s of [state, trimmed]) {
      if (s && !candidates.includes(s)) {
        candidates.push(s)
      }
    }
    try {
      const decoded = decodeURIComponent(trimmed)
      if (decoded && !candidates.includes(decoded)) {
        candidates.push(decoded)
      }
    } catch {
      // ignore malformed % sequences
    }
    for (const c of candidates) {
      if (this._sessions.has(c)) {
        return c
      }
    }
    return null
  }

  private getSessionForState(state: string): SessionData | undefined {
    const key = this.resolveSessionKey(state)
    return key ? this._sessions.get(key) : undefined
  }

  /**
   * Generate authorization URL for OAuth2 flow and validate with Epic FHIR
   *
   * @param iss - Issuer URL
   * @param launch - Launch token
   * @returns Promise with redirect URL from Epic FHIR after validation
   */
  async retrieveSmartOnFHIRRedirectUrl(iss: string, launch: string): Promise<string> {
    // Call EpicFhirAuthorization endpoint
    const requestBody = {
      iss,
      launch,
      state: this.instanceState,
    }

    const response = await this.providerAPIClient.post(
      '/EpicFhirAuthorization',
      requestBody,
      {
        'Content-Type': 'application/json',
      },
      false
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(`API Gateway request failed: ${response.status} - ${errorData.message || response.statusText}`)
    }

    const responseData = (await response.json()) as AuthorizationResponse

    // Validate required fields
    if (
      !responseData.codeVerifier ||
      !responseData.clientId ||
      !responseData.redirectUri ||
      !responseData.authorizeUrl ||
      !responseData.organizationId ||
      !responseData.fhirServerUrl
    ) {
      throw new Error(
        'API Gateway response missing required fields: codeVerifier, codeChallenge, clientId, redirectUri, authorizeUrl, organizationId, and/or fhirServerUrl'
      )
    }

    // Store session with data from API response
    this.setSession(this.instanceState, {
      fhirServer: responseData.fhirServerUrl,
      codeVerifier: responseData.codeVerifier,
      redirectUri: responseData.redirectUri,
      orgId: responseData.organizationId,
      accessToken: null,
      context: {
        fhirPatientId: null,
        fhirUserId: null,
        epicUserId: null,
        epicUserFhirId: null,
        userEmail: null,
        userName: null,
        userLastName: null,
        providerNpi: null,
        providerType: null,
        loginDepartment: null,
        epicVersion: null,
        apiKey: null,
        mrnIdentifierSystem: null,
        clientName: null,
        encounterCsn: null,
        clientHostSource: null,
      },
    })

    return responseData.authorizeUrl
  }

  /**
   * Exchange authorization code for access token
   *
   * @param authorizationCode - The authorization code from the callback
   * @param state - The state parameter for session validation
   * @param orgId - The organization ID for Epic Sandbox
   * @returns Access response with patient and user IDs
   */
  async retrieveSmartOnFHIRAccessContext(
    authorizationCode: string,
    state: string,
    orgId: string
  ): Promise<AccessTokenResponse> {
    const sessionKey = this.resolveSessionKey(state)
    if (!sessionKey) {
      throw new Error(
        'Invalid state parameter or session expired. ' +
          'Confirm /launch and /epic-sandbox/callback use the same origin (e.g. https://localhost:5173, not 127.0.0.1), ' +
          'and that client-config epicLandingCallback matches that URL so the PKCE session is not lost across redirects.'
      )
    }

    const session = this._sessions.get(sessionKey)!
    const codeVerifier = session.codeVerifier
    const redirectUri = session.redirectUri

    const requestBody = {
      authorizationCode: authorizationCode,
      redirectUri: redirectUri,
      codeVerifier: codeVerifier,
      orgId: orgId,
    }

    const response = await this.providerAPIClient.post(
      '/EpicFhirAccessToken',
      requestBody,
      {
        'Content-Type': 'application/json',
      },
      false
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(`API Gateway request failed: ${response.status} - ${errorData.message || response.statusText}`)
    }
    const contents = (await response.json()) as AccessTokenResponse & { access_token?: string }

    // Validate required fields
    if (!contents.access_token) {
      throw new Error('API Gateway response missing required field: access_token')
    }

    const providerOrEpicJwt = contents.jwtToken ?? contents.token
    if (!providerOrEpicJwt) {
      console.warn('[EpicAuthClient] Epic OAuth response missing jwtToken/token - using API key fallback')
      if (!contents.apiKey) {
        throw new Error('API Gateway response missing both jwtToken/token and apiKey')
      }
    }

    // Update session with access token and context.
    // Do not set session.orgId from the callback argument: EpicFhirAuthorization (esp. Lambda)
    // may return IMDB UUID as organizationId; orgId is corrected from JWT clientConfigKey after epic-provision.
    session.accessToken = contents.access_token
    // Prefer the true expiry from the access token's own `exp` claim; leave
    // null (never-expires from the lockout's perspective) if it can't be read.
    session.accessTokenExpiresAt = decodeJwtExpiryMs(contents.access_token)
    session.context.fhirPatientId = contents.fhirPatientId
    session.context.fhirUserId = contents.fhirUserId
    session.context.epicUserId = contents.epicUserId
    session.context.epicUserFhirId = contents.epicUserFhirId
    session.context.userEmail = contents.userEmail
    session.context.userName = contents.userName
    session.context.userLastName = contents.userLastName
    session.context.providerNpi = contents.providerNpi
    session.context.providerType = contents.providerType
    session.context.loginDepartment = contents.loginDepartment
    session.context.epicVersion = contents.epicVersion
    session.context.mrnIdentifierSystem = contents.mrnIdentifierSystem
    session.context.clientName = contents.clientName
    session.context.encounterCsn = contents.encounterCsn ?? null
    session.context.clientHostSource = normalizeClientHostSource(contents.clientHostSource)

    // Capture the raw Epic-signed id_token (identity artifact for provider-api
    // auth ONLY). Kept strictly for the epic-provision path; never a FHIR bearer.
    session.epicIdToken = contents.epicIdToken ?? null

    // Store Epic JWT from Lambda (used for epic-provision and as fallback)
    if (providerOrEpicJwt) {
      session.jwtToken = providerOrEpicJwt
      console.log('[EpicAuthClient] Epic JWT received; calling /auth/epic-provision for provider-api token')
      // Call provider-api epic-provision to get Auth0-compatible JWT (find user by NPI, get permissions)
      const providerApiJwt = await this.callEpicProvision(providerOrEpicJwt, session.epicIdToken)
      if (providerApiJwt) {
        session[PROVIDER_API_JWT_KEY] = providerApiJwt
        console.log('[EpicAuthClient] Provider-api JWT from epic-provision stored for provider-api authentication')
        applyProvisioningClaimsToSession(session, providerApiJwt)
      } else {
        console.warn('[EpicAuthClient] epic-provision failed or unavailable; provider-api may 401 until retry')
      }
    }

    // Store API key if present (backward compatibility)
    if (contents.apiKey) {
      session.context.apiKey = contents.apiKey
      console.log('[EpicAuthClient] API key received (backward compatibility mode)')
    }

    // Save updated session to storage and record the active OAuth state for
    // post-callback navigation and page reloads within this tab.
    // Write to both sessionStorage (fast, tab-scoped) and localStorage so that
    // Epic iframe re-navigations that clear sessionStorage can still recover
    // the active state without ambiguity.
    this.saveSessionsToStorage()
    sessionStorage.setItem(ACTIVE_OAUTH_STATE_STORAGE_KEY, sessionKey)
    localStorage.setItem(ACTIVE_OAUTH_STATE_STORAGE_KEY, sessionKey)

    return {
      message: 'Successfully retrieved access token',
      fhirPatientId: contents.fhirPatientId,
      fhirUserId: contents.fhirUserId,
      epicUserId: contents.epicUserId,
      epicUserFhirId: contents.epicUserFhirId,
      userEmail: contents.userEmail,
      userName: contents.userName,
      userLastName: contents.userLastName,
      providerNpi: contents.providerNpi,
      providerType: contents.providerType,
      loginDepartment: contents.loginDepartment,
      epicVersion: contents.epicVersion,
      apiKey: contents.apiKey,
      mrnIdentifierSystem: contents.mrnIdentifierSystem,
      orgId: session.orgId ?? orgId,
      clientName: contents.clientName,
      encounterCsn: contents.encounterCsn,
      clientHostSource: session.context.clientHostSource,
    }
  }

  /**
   * Get access token for a given state
   *
   * @param state - The state parameter
   * @returns Access token or null if not found
   */
  getAccessToken(state: string): string | null {
    const session = this.getSessionForState(state)
    return session?.accessToken || null
  }

  /**
   * Epoch millis at which this session's Epic access token expires, or null when
   * unknown (no successful token exchange recorded yet, no session, or the token
   * lacked a decodable `exp` claim).
   *
   * Sourced from the access token's own `exp` claim, decoded at exchange time.
   */
  getAccessTokenExpiresAt(state: string): number | null {
    const session = this.getSessionForState(state)
    if (!session || typeof session.accessTokenExpiresAt !== 'number') {
      return null
    }
    return session.accessTokenExpiresAt
  }

  /**
   * True when the Epic access token for `state` has passed its expiry minus the
   * safety buffer ({@link EPIC_ACCESS_TOKEN_EXPIRY_BUFFER_MS}). Sessions with no
   * known expiry are treated as NOT expired (e.g. pre-authorization, a token
   * with no decodable `exp`, or legacy sessions persisted before expiry tracking
   * existed) so we never lock out a session we cannot reason about.
   *
   * @param now - Injectable clock for testing; defaults to `Date.now()`.
   */
  isAccessTokenExpired(state: string, now: number = Date.now()): boolean {
    const expiresAt = this.getAccessTokenExpiresAt(state)
    if (expiresAt === null) {
      return false
    }
    return now >= expiresAt - EPIC_ACCESS_TOKEN_EXPIRY_BUFFER_MS
  }

  /**
   * Epoch millis at which the session will be locked out (expiry minus the
   * safety buffer), or null when unknown. This is the moment
   * {@link isAccessTokenExpired} starts returning true.
   */
  getAccessTokenLockoutAt(state: string): number | null {
    const expiresAt = this.getAccessTokenExpiresAt(state)
    if (expiresAt === null) {
      return null
    }
    return expiresAt - EPIC_ACCESS_TOKEN_EXPIRY_BUFFER_MS
  }

  /**
   * True when the session is inside the pre-lockout warning window — i.e. within
   * {@link EPIC_ACCESS_TOKEN_EXPIRY_WARNING_MS} of the lockout point but not yet
   * locked out. Used to prompt the user to save in-progress work. Returns false
   * for sessions with no known expiry (see {@link isAccessTokenExpired}).
   *
   * @param now - Injectable clock for testing; defaults to `Date.now()`.
   */
  isAccessTokenExpiryWarning(state: string, now: number = Date.now()): boolean {
    const lockoutAt = this.getAccessTokenLockoutAt(state)
    if (lockoutAt === null) {
      return false
    }
    if (now >= lockoutAt) {
      // Already locked out — the lockout screen takes over, not the warning.
      return false
    }
    return now >= lockoutAt - EPIC_ACCESS_TOKEN_EXPIRY_WARNING_MS
  }

  getFhirServerUrl(state: string): string {
    const session = this.getSessionForState(state)
    return session?.fhirServer || ''
  }

  /**
   * Get organization ID for a given state
   *
   * @param state - The state parameter
   * @returns Organization ID or null if not found
   */
  getOrgId(state: string): string | null {
    const session = this.getSessionForState(state)
    return session?.orgId || null
  }

  /** IMDB organization UUID from provisioned JWT (membership); null if not decoded yet. */
  getImdbOrganizationId(state: string): string | null {
    const session = this.getSessionForState(state)
    return session?.imdbOrganizationId ?? null
  }

  /**
   * Get context for a given state
   *
   * @param state - The state parameter
   * @returns Context object with patient and user IDs
   */
  getContext(state: string): {
    fhirPatientId: string | null
    fhirUserId: string | null
    epicUserId: string | null
    epicUserFhirId: string | null
    userEmail: string | null
    userName: string | null
    userLastName: string | null
    providerNpi: string | null
    providerType: string | null
    loginDepartment: string | null
    epicVersion: string | null
    apiKey: string | null
    mrnIdentifierSystem: string | null
    clientName: string | null
    encounterCsn: string | null
    orgId: string | null
    clientHostSource: string | null
  } {
    const session = this.getSessionForState(state)
    return {
      fhirPatientId: session?.context.fhirPatientId || null,
      fhirUserId: session?.context.fhirUserId || null,
      epicUserId: session?.context.epicUserId || null,
      epicUserFhirId: session?.context.epicUserFhirId || null,
      userEmail: session?.context.userEmail || null,
      userName: session?.context.userName || null,
      userLastName: session?.context.userLastName || null,
      providerNpi: session?.context.providerNpi || null,
      providerType: session?.context.providerType || null,
      loginDepartment: session?.context.loginDepartment || null,
      epicVersion: session?.context.epicVersion || null,
      apiKey: session?.context.apiKey || null,
      mrnIdentifierSystem: session?.context.mrnIdentifierSystem || null,
      clientName: session?.context.clientName || null,
      encounterCsn: session?.context.encounterCsn || null,
      orgId: session?.orgId || null,
      clientHostSource: session?.context.clientHostSource ?? null,
    }
  }

  /**
   * Get Epic JWT from Lambda (for FHIR or for calling epic-provision).
   */
  getJwtToken(state: string): string | null {
    const session = this.getSessionForState(state)
    return session?.jwtToken || null
  }

  /**
   * Raw Epic-signed OIDC id_token for this session (identity artifact for
   * provider-api auth ONLY — forwarded to epic-provision as X-Epic-Id-Token).
   * Never a FHIR bearer and never the provider-api access token.
   */
  getEpicIdToken(state: string): string | null {
    const session = this.getSessionForState(state)
    return session?.epicIdToken || null
  }

  /**
   * Call provider-api POST /auth/epic-provision with the Epic JWT; returns the
   * Auth0-compatible jwtToken or null.
   *
   * The raw Epic id_token (when present) is forwarded as the `X-Epic-Id-Token`
   * header so the Auth0 Custom Token Exchange Action can verify it against Epic's
   * JWKS. When it is absent (older server / flag OFF), the request is still sent
   * (the server decides / 503s under its ON flag) but a warning is logged — the
   * legacy HS256 path must not be blocked.
   */
  private async callEpicProvision(epicJwt: string, epicIdToken?: string | null): Promise<string | null> {
    const baseUrl = import.meta.env.VITE_PROVIDER_API_URL
    if (!baseUrl) {
      logger.warn('VITE_PROVIDER_API_URL not set; skipping epic-provision')
      return null
    }
    const headers: Record<string, string> = {
      Authorization: `Bearer ${epicJwt}`,
      'Content-Type': 'application/json',
    }
    if (epicIdToken) {
      headers['X-Epic-Id-Token'] = epicIdToken
    } else {
      console.warn(
        '[EpicAuthClient] epic-provision called without an Epic id_token; sending anyway (server decides under its flag)'
      )
    }
    try {
      const res = await fetch(`${baseUrl}/auth/epic-provision`, {
        method: 'POST',
        headers,
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        logger.warn('epic-provision failed', { status: res.status, body })
        return null
      }
      const data = (await res.json()) as { jwtToken?: string; token?: string }
      return data.jwtToken ?? data.token ?? null
    } catch (err) {
      logger.warn('epic-provision request error', { error: err instanceof Error ? err.message : String(err) })
      return null
    }
  }

  /**
   * Get or refresh the Auth0-compatible JWT for provider-api (Epic launch).
   * Returns cached providerApiJwtToken if set; otherwise calls epic-provision with
   * the Epic JWT (+ id_token) and caches the result.
   *
   * @param options.forceRefresh - When true, ignore any cached provider-api token
   *   and force a fresh epic-provision call (the server re-runs the token exchange).
   *   Used by the bounded 401 re-exchange path. Returns null if no fresh token can
   *   be obtained (e.g. epic-provision 503 / no Epic JWT).
   */
  async getOrRefreshProviderApiToken(state: string, options?: { forceRefresh?: boolean }): Promise<string | null> {
    const session = this.getSessionForState(state)
    if (!session) return null
    if (!options?.forceRefresh) {
      const cached = session[PROVIDER_API_JWT_KEY]
      if (cached) return cached
    }
    const epicJwt = session.jwtToken
    if (!epicJwt) return null

    // Dedup concurrent provisions for this session: if one is already running,
    // await it instead of firing another POST /auth/epic-provision. Keyed by the
    // resolved session key so all callers for the same session share one call.
    // forceRefresh joins the same in-flight call rather than starting a parallel one.
    const inflightKey = this.resolveSessionKey(state) ?? state
    const existing = this._inFlightProvision.get(inflightKey)
    if (existing) return existing

    const provisionPromise = (async () => {
      const token = await this.callEpicProvision(epicJwt, session.epicIdToken)
      if (token) {
        session[PROVIDER_API_JWT_KEY] = token
        applyProvisioningClaimsToSession(session, token)
        this.saveSessionsToStorage()
      }
      return token
    })()
    this._inFlightProvision.set(inflightKey, provisionPromise)
    try {
      return await provisionPromise
    } finally {
      // Clear on success OR failure so a failed provision never wedges retries.
      this._inFlightProvision.delete(inflightKey)
    }
  }

  /**
   * Check if a session exists for a given state
   *
   * @param state - The state parameter
   * @returns True if session exists, false otherwise
   */
  hasSession(state: string): boolean {
    return this.resolveSessionKey(state) !== null
  }
  /**
   * Load sessions from shared localStorage entry.
   */
  private loadSessionsFromStorage(): void {
    try {
      const storedSessions = localStorage.getItem(SESSIONS_STORAGE_KEY)
      if (storedSessions) {
        const sessionsData = JSON.parse(storedSessions)
        this._sessions = new Map(Object.entries(sessionsData))
        this.syncOrgIdsFromStoredProviderJwts()
      }
    } catch (error) {
      logger.warn('Failed to load sessions from localStorage', {
        error: error instanceof Error ? error.message : String(error),
      })
    }
  }

  /**
   * Fix orgId on reload: EpicFhirAuthorization may have stored IMDB UUID; provisioned JWT carries
   * `{iss}/clientConfigKey` (Dynamo partition key). Persists when corrections are applied.
   */
  private syncOrgIdsFromStoredProviderJwts(): void {
    let dirty = false
    for (const session of this._sessions.values()) {
      const jwt = session[PROVIDER_API_JWT_KEY]
      if (!jwt || typeof jwt !== 'string') continue
      const prevOrg = session.orgId
      const prevImdb = session.imdbOrganizationId
      applyProvisioningClaimsToSession(session, jwt)
      if (session.orgId !== prevOrg || session.imdbOrganizationId !== prevImdb) {
        dirty = true
      }
    }
    if (dirty) {
      this.saveSessionsToStorage()
    }
  }

  /**
   * Save sessions to shared localStorage entry.
   */
  private saveSessionsToStorage(): void {
    try {
      const sessionsData = Object.fromEntries(this._sessions)
      localStorage.setItem(SESSIONS_STORAGE_KEY, JSON.stringify(sessionsData))
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(EPIC_FHIR_SESSIONS_UPDATED_EVENT))
      }
    } catch (error) {
      logger.warn('Failed to save sessions to localStorage', {
        error: error instanceof Error ? error.message : String(error),
      })
    }
  }

  /**
   * Set session with persistence
   */
  private setSession(state: string, sessionData: SessionData): void {
    this._sessions.set(state, sessionData)
    this.saveSessionsToStorage()
  }

  /**
   * Returns the single active session state key when exactly one Epic session
   * exists in the in-memory map (loaded from localStorage).
   *
   * Used as a last-resort fallback by `getActiveEpicState()` when
   * `sessionStorage` has been cleared (e.g. Epic iframe re-navigation) but the
   * full session is still present in localStorage.  Returns null when there are
   * zero or multiple sessions (ambiguous which is "active").
   */
  getFirstSessionState(): string | null {
    const keys = Array.from(this._sessions.keys())
    return keys.length === 1 ? keys[0] : null
  }

  /**
   * Remove session with persistence
   */
  removeSession(state: string): void {
    this._sessions.delete(state)
    this.saveSessionsToStorage()
  }

  /**
   * Clear all sessions (useful for logout).
   * Also removes the persisted active OAuth state from sessionStorage so that the
   * next page load starts a fresh launch.
   */
  clearAllSessions(): void {
    this._sessions.clear()
    localStorage.removeItem(SESSIONS_STORAGE_KEY)
    localStorage.removeItem(ACTIVE_OAUTH_STATE_STORAGE_KEY)
    sessionStorage.removeItem(ACTIVE_OAUTH_STATE_STORAGE_KEY)
  }
}
